import { useNavigate } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import { useState, useEffect, useContext, useRef } from "react";
import AuthContext from "../../store/auth-context";
import { GET, PUT, DEL } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import EntityConstants from "../../configs/EntityConstants";
import PlusCircle from "../../resources/icons/plus-circle.svg";
import CreateProgrammeModal from "../../common/CreateProgrammeModal/CreateProgrammeModal";
import useNavigationStore from "../../store/navigationStore";
import DraftsRevokeModal from "../../common/DraftsRevokeModal/DraftsRevokeModal";
import useSearchDebounce from "../../hooks/useSearchDebounce";

export default function ProgrammePage() {
  const authContext = useContext(AuthContext);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const setProgrammeInStore = useNavigationStore((state) => state.setProgramme);

  const [searchTerm, setSearchTerm] = useState("");
  const [programmes, setProgrammes] = useState([]);
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [batchName, setBatchName] = useState("Batches");
  const [showDropdown, setShowDropdown] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentProgramme, setCurrentProgramme] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(null);

  const fetchProgrammes = (limit, offset, search = "") => {
    setIsLoading(true);
    const endpoint = search
      ? `${EntityConstants.GET_PROGRAMME_DETAILS}?limit=${limit}&offset=${offset}&search=${search}`
      : `${EntityConstants.GET_PROGRAMME_DETAILS}?limit=${limit}&offset=${offset}`;
    GET(endpoint)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          Toast({
            type: "error",
            title: "Error",
            message: data.error,
          });
        } else {
          const activeProgrammes = data.data.results.filter(
            (programme) => programme.is_active
          );
          setProgrammes(activeProgrammes);
          setPrevious(data.data.previous);
          setNext(data.data.next);
          authContext.ProgrammeOffsetHandler(offset);
        }
      })
      .catch((error) => {
        console.error(error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to fetch programmes",
        });
      })
      .finally(() => {
        setIsLoading(false);
        setIsSearching(false);
      });
  };

  const performSearchAPI = async (searchTerm, signal) => {
    try {
      const endpoint = `${EntityConstants.GET_PROGRAMME_DETAILS}?limit=8&offset=0&search=${searchTerm}`;
      const response = await GET(endpoint, { signal });
      const data = await response.json();

      if (data.error) {
        Toast({
          type: "error",
          title: "Error",
          message: data.error,
        });
        setProgrammes([]);
      } else {
        const activeProgrammes = data.data.results.filter(
          (programme) => programme.is_active
        );
        setProgrammes(activeProgrammes);
        setPrevious(data.data.previous);
        setNext(data.data.next);
        authContext.ProgrammeOffsetHandler(0);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to fetch programmes",
        });
        setProgrammes([]);
      }
    }
  };

  const { handleSearch } = useSearchDebounce(performSearchAPI, 500);

  const handleProgrammeCreated = () => {
    fetchProgrammes(8, authContext.programmeOffset || 0);
  };

  const handleEditProgramme = (programme) => {
    setCurrentProgramme(programme);
    setEditModalOpen(true);
    setShowDropdown(null);
  };

  const deleteProgramme = async (programmeCode) => {
    setIsDeleting(true);

    const payload = {
      programme_code: programmeCode,
    };

    try {
      const response = await DEL(
        `${EntityConstants.GET_PROGRAMME_DETAILS}?programme_id=${programmeCode}`,
        payload
      );
      const data = await response.json();

      if (response.ok) {
        Toast({
          type: "success",
          title: "Success",
          message: "Programme deleted successfully",
        });
        const currentOffset = authContext.programmeOffset || 0;
        const pageSize = 8;

        if (programmes.length === 1 && currentOffset > 0) {
          const newOffset = Math.max(0, currentOffset - pageSize);
          fetchProgrammes(pageSize, newOffset, searchTerm);
        } else {
          fetchProgrammes(pageSize, currentOffset, searchTerm);
        }
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: data.error || "Failed to delete programme",
        });
      }
    } catch (error) {
      console.error(error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to delete programme",
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(null);
      setCurrentProgramme(null);
    }
  };

  const handleDeleteProgramme = (programme) => {
    setCurrentProgramme(programme);
    setIsDeleteModalOpen(programme.programme_code);
    setShowDropdown(null);
  };

  const confirmDeleteProgramme = () => {
    if (currentProgramme) {
      deleteProgramme(currentProgramme.programme_code);
    }
  };

  const handleProgrammeUpdate = (updatedName, updatedDescription) => {
    const payload = {
      programme_code: currentProgramme.programme_code,
      programme_name: updatedName,
      description: updatedDescription,
    };

    PUT(EntityConstants.GET_PROGRAMME_DETAILS, payload)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          Toast({
            type: "error",
            title: "Error",
            message: data.error,
          });
        } else {
          Toast({
            type: "success",
            title: "Success",
            message: "Programme updated successfully",
          });
          fetchProgrammes(8, authContext.programmeOffset || 0);
        }
      })
      .catch((error) => {
        console.error(error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to update programme",
        });
      });
  };

  const fetchBatchName = () => {
    GET(EntityConstants.GET_BATCH_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.batch_display_name) {
          setBatchName(data.batch_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching batch name:", error);
      });
  };

  useEffect(() => {
    fetchBatchName();
  }, []);

  useEffect(() => {
    if (authContext.programmeOffset !== null) {
      localStorage.setItem("programmeOffset", authContext.programmeOffset);
    }
  }, [authContext.programmeOffset]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
      }
    };

    if (showDropdown !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleProgrammeLinkClick = (programme) => {
    setProgrammeInStore(programme);
    navigate(`/issuer/workspace/batches/${programme.programme_code}`);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value ? e.target.value : "";
    setSearchTerm(value);
    authContext.ProgrammeOffsetHandler(0);

    if (value.length > 0) {
      setIsSearching(true);
      handleSearch(value, {
        onStart: () => setIsSearching(true),
        onComplete: () => setIsSearching(false),
        onError: () => {
          setProgrammes([]);
          setIsSearching(false);
        },
        minChars: 1,
      });
    } else {
      setIsSearching(false);
      fetchProgrammes(8, 0);
    }
  };

  useEffect(() => {
    if (!searchTerm) {
      fetchProgrammes(8, authContext.programmeOffset || 0);
    }
  }, [authContext.programmeOffset]);

  return (
    <div className="w-full mt-20 bg-cfSecondary min-h-screen">
      <div className="">
        <div
          className={`w-full bg-cfSearchContainer flex justify-center py-[0.7rem] px-4`}
        >
          <div
            className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
          >
            <p className="text-cfHeadingText font-semibold self-start my-auto">
              Workspace
            </p>

            {isModalOpen && (
              <CreateProgrammeModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onProgrammeCreated={handleProgrammeCreated}
              />
            )}
            <div className="flex flex-col sm:flex-row items-start sm:mt-0 gap-2 sm:gap-4 w-full sm:w-auto">
              <button
                onClick={() => setIsModalOpen(true)}
                className="h-[2.75rem] min-w-[12.5rem] px-2 gap-2 bg-[#3538CD] text-white rounded-[8px] flex items-center justify-center hover:bg-cfPrimaryDark transition duration-200 border border-gray-800"
              >
                <img src={PlusCircle} className="h-4 w-4" alt="" />
                <span className="font-medium">Create Program</span>
              </button>
              <div className="bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem]">
                <input
                  type="text"
                  name="search"
                  id="search"
                  placeholder={`Search for programmes...`}
                  className="outline-none border-none p-0 placeholder:text-sm w-full"
                  style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                  onChange={handleSearchInputChange}
                  value={searchTerm}
                />
              </div>
            </div>
          </div>
        </div>

        {isLoading || isSearching ? (
          <div className="col-span-full w-full flex justify-center items-center py-4">
            <CfLoader />
          </div>
        ) : programmes.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-4 mt-4">
              {programmes
                .filter((programme) => programme.is_active)
                .map((programme) => (
                  <div
                    key={programme.programme_code}
                    onClick={() => handleProgrammeLinkClick(programme)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleProgrammeLinkClick(programme);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="border rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer bg-white h-full flex flex-col"
                  >
                    <div className="h-[6rem] relative flex items-start justify-center top-3">
                      <div className="absolute inset-0 bg-gradient-to-t border-b mx-3"></div>
                      <div className="absolute left-[1rem] right-[1rem]">
                        <div className="group flex justify-between items-center relative">
                          <h3 className="font-semibold text-cfPrimaryButton transition-colors text-lg line-clamp-3 pr-6">
                            {programme.programme_name}
                          </h3>
                          <div className="absolute right-0 top-0 h-full flex items-start">
                            <div className="relative">
                              <MoreVertical
                                className="h-[1.1rem] w-[1.1rem] mt-2 cursor-pointer hover:text-cfPrimaryButton transition-all duration-200"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowDropdown(
                                    showDropdown === programme.programme_code
                                      ? null
                                      : programme.programme_code
                                  );
                                }}
                              />
                              {showDropdown === programme.programme_code && (
                                <div
                                  ref={dropdownRef}
                                  className="absolute right-0 mt-2 w-28 bg-white rounded-md shadow-lg z-10 border"
                                >
                                  <div>
                                    <button
                                      className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleEditProgramme(programme);
                                      }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="block w-full text-left border-t border-gray-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-gray-100 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDeleteProgramme(programme);
                                      }}
                                      disabled={isDeleting}
                                    >
                                      {isDeleting ? "Deleting..." : "Delete"}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 flex-1 flex flex-col">
                      <div>
                        <p className="text-sm text-gray-500 mt-[0.5rem] mb-[1rem] truncate">
                          {programme.description}
                        </p>
                      </div>
                      <div className="mt-auto flex flex-wrap gap-5">
                        <div className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                          {programme.number_of_batches}{" "}
                          {batchName ? batchName : "Batches"}
                        </div>
                        <div className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                          {programme.number_of_templates} Templates
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <div className="flex items-center justify-between mt-6 w-full px-4 pb-4">
              <button
                onClick={() =>
                  authContext.ProgrammeOffsetHandler(
                    authContext.programmeOffset - 8
                  )
                }
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={previous === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                    />
                  </svg>
                  <span>previous</span>
                </span>
              </button>

              <button
                onClick={() =>
                  authContext.ProgrammeOffsetHandler(
                    authContext.programmeOffset + 8
                  )
                }
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={next === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <span>Next</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </>
        ) : (
          <div className="mt-6 w-full flex justify-center">
            <p className="text-lg font-semibold text-cfSignUpText">
              No programmes found
            </p>
          </div>
        )}
      </div>

      {editModalOpen && currentProgramme && (
        <CreateProgrammeModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onProgrammeCreated={() => {
            fetchProgrammes(8, authContext.programmeOffset || 0);
            setEditModalOpen(false);
          }}
          isEditMode={true}
          initialName={currentProgramme.programme_name}
          initialDescription={currentProgramme.description}
          onUpdate={handleProgrammeUpdate}
        />
      )}
      <DraftsRevokeModal
        isOpen={isDeleteModalOpen !== null}
        onClose={() => {
          setIsDeleteModalOpen(null);
          setCurrentProgramme(null);
        }}
        onConfirm={confirmDeleteProgramme}
        title="Delete Programme"
        description={`Are you sure you want to delete the programme "${currentProgramme?.programme_name}"? This action cannot be undone.`}
      />
    </div>
  );
}
